/**
 * LayoutEditorControls
 *
 * Per-element control strip of the layout editor: add/remove, visibility,
 * protection, workflow-button wiring, and the Configure Columns drawer for table
 * elements. Visibility is one `ViewVisibilityControl` over the org-wide pool, and
 * the internal-marker warning is the control's — it re-evaluates on every render
 * instead of only on change. Its `Open in matrix` deep-links into the Bulk Edit
 * tab: the element's `dndId` is the matrix row id there, so setting
 * `visibilityMatrixRow` before `setMode('BulkEdit')` lands the user on the row
 * they were editing. Consumers are the layout editor element shells, which mount it under
 * `LayoutEditorProvider` — the context is required and throws when absent. The
 * Configure Columns drawer is drag-resizable; `uiState` owns the width, outliving
 * this component, so it survives `destroyOnHidden` unmounting `ColumnConfigEditor`
 * and persists per user under `panelWidth-layoutColumnConfig`, and
 * `drawerDraggerStyle` makes the drag handle visible.
 */

import { FC, useContext, useMemo, useState } from 'react';
import { Button, Checkbox, Select, Typography, Divider, Drawer, Input, theme } from 'antd';
import { DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';

import { EditorUIElement, EditorUIContainer, UIElementType, UIArTable, UIField, UIEntityReference, UIButton, WorkflowMenuSection } from '#types/System.types';
import { formStateDescription, workflowMenuSectionLabels, type FormStateId } from '#types';
import { useApplicableWorkflows } from '../../use/useApplicableWorkflows';
import { isCloseAndCreateTargetState } from '../../use/form/isCloseAndCreateTargetState';
import { LayoutEditorContext } from '../../providers/editor/LayoutEditorContext';
import { FieldTypeContext } from '../../providers/FieldTypeContext';
import { AddElementMenu } from './element/AddElementMenu';
import { VALUE_ELEMENT_TYPES, CONTAINER_ELEMENT_TYPES, ALWAYS_REQUIRED_ELEMENT_TYPES, MetaFieldUIDescription } from '#types/System.Parameters.types';
import FlexCol from '../atoms/FlexCol';
import { ViewVisibilityControl, type ViewVisibility } from '../visibility';
import { drawerDraggerStyle } from '../atoms/drawerDraggerStyle';
import ColumnConfigEditor from './ColumnConfigEditor';
import {
    panelMaxWidth,
    panelViewportClamp,
    setPanelWidth,
    uiState,
} from '../../store/uiState';

const { Text } = Typography;


interface LayoutEditorControlsProps {
    element: EditorUIElement;
    parentElement: EditorUIElement | null;
}

/**
 * Controls component for the layout editor that provides element manipulation options
 * Displays add and remove controls for container elements and remove controls for fields
 * @component
 * @param {LayoutEditorControlsProps} props - Props for the LayoutEditorControls component
 * @returns {JSX.Element} The rendered LayoutEditorControls component
 */
export const LayoutEditorControls: FC<LayoutEditorControlsProps> = ({
    element,
    parentElement,
}) => {

    const editor = useContext(LayoutEditorContext);
    if (!editor) throw new Error('Editor must be used within LayoutEditorProvider');

    const { removeElement, tableTypes, layoutEditorState } = editor;
    const { token } = theme.useToken();
    const formTypeId = editor.type === 'form' ? editor.selectedStructureId : undefined;
    const { fieldTypes } = useContext(FieldTypeContext);
    const isAlwaysRequired = ALWAYS_REQUIRED_ELEMENT_TYPES.includes(element.uiElementType);
    const isContainerElement = CONTAINER_ELEMENT_TYPES.includes(element.uiElementType);
    const isValueElement = VALUE_ELEMENT_TYPES.includes(element.uiElementType);
    const isVisibilitySupported = VALUE_ELEMENT_TYPES.includes(element.uiElementType);

    const fieldType = 'fieldId' in element ? fieldTypes[element.fieldId] : undefined;
    const isTableElement = element.uiElementType === UIElementType.Table;
    const tableType = isTableElement ? tableTypes.find(t => t.docId === (element as UIArTable).tableId) : undefined;

    const isButtonElement = element.uiElementType === UIElementType.Button;
    const buttonElement = isButtonElement ? (element as UIButton) : undefined;
    const { workflows: buttonWorkflows } = useApplicableWorkflows({
        triggerType: 'formAction',
        formType: formTypeId,
        enabled: isButtonElement,
    });

    const sectionOptions = (Object.keys(workflowMenuSectionLabels) as WorkflowMenuSection[])
        .map((value) => ({ value, label: workflowMenuSectionLabels[value] }));
    const formTypeStructures = useMemo(
        () => (editor.type === 'form' ? editor.structures : []),
        [editor]
    );
    const buttonItemOptions = useMemo<{ value: string; label: string }[]>(() => {
        const sectionSel = buttonElement?.menuSection;
        if (!sectionSel) return [];
        if (sectionSel === 'share') {
            return [
                { value: 'email', label: 'Send via Email' },
                { value: 'pdf', label: 'Export as PDF' },
                { value: 'exportTable', label: 'Export Tables to XLSX' },
            ];
        }
        if (sectionSel === 'workflows') {
            return buttonWorkflows
                .filter((w): w is typeof w & { docId: string } => !!w.docId)
                .map(w => ({
                    value: `workflow-${w.docId}`,
                    label: w.description?.shortLabel ?? w.docId,
                }));
        }
        if (sectionSel === 'form') {
            return (layoutEditorState.allowedNextForms ?? [])
                .filter((ftId): ftId is string => !!ftId)
                .map(ftId => ({
                    value: `form-${ftId}`,
                    label: formTypeStructures.find(ft => ft.docId === ftId)?.description?.shortLabel ?? ftId,
                }));
        }
        if (sectionSel === 'state') {
            const states = layoutEditorState.possibleStates ?? {};
            const targets = new Set<FormStateId>();
            for (const stateId of Object.keys(states) as FormStateId[]) {
                for (const next of states[stateId]?.allowedNextStates ?? []) targets.add(next);
            }
            return Array.from(targets)
                .filter(isCloseAndCreateTargetState)
                .map(st => ({ value: `state-${st}`, label: formStateDescription[st]?.shortLabel ?? st }));
        }
        return [
            { value: 'state-draft', label: 'New draft version' },
            { value: 'goto-current-version', label: 'Go to Current Version' },
        ];
    }, [buttonElement?.menuSection, buttonWorkflows, layoutEditorState.allowedNextForms, layoutEditorState.possibleStates, formTypeStructures]);

    const [columnConfigOpen, setColumnConfigOpen] = useState(false);

    const { width } = useSnapshot(uiState.layoutColumnConfig, { sync: true });

    const isTaggedInternal = Boolean(
        ('internalVisibilityTag' in element ? element.internalVisibilityTag : false)
        || (element.uiElementType === UIElementType.Field ? fieldType?.internalVisibilityTag : false)
    );
    const isInternalTagEditable = !(element.uiElementType === UIElementType.Field && Boolean(fieldType?.internalVisibilityTag));

    const handleRequiredChange = (checked: boolean) => {
        if (!isValueElement) return;
        editor.updateRequiredState(element.dndId, checked);
    };

    const currentVisibilityTypes = useMemo(() => {
        if (!('visibilityTypes' in element)) return [];
        return element.visibilityTypes ?? [];
    }, [element]);

    const handleVisibilityChange = (next: ViewVisibility) => {
        if (!isValueElement || !isVisibilitySupported) return;
        editor.updateVisibilityTypes(element.dndId, next.visibilityTypes ?? []);
    };

    const handleOpenMatrix = () => {
        editor.setVisibilityMatrixRow(element.dndId);
        editor.setMode('BulkEdit');
    };

    const handleInternalOnlyChange = (checked: boolean) => {
        if (!isValueElement) return;
        editor.updateInternalOnly(element.dndId, checked);
    };

    const handleTriggerSearchSyncChange = (checked: boolean) => {
        const isField = element.uiElementType === UIElementType.Field;
        const isEntityReference = element.uiElementType === UIElementType.EntityReference;
        const isEmbeddedEntity = element.uiElementType === UIElementType.EmbeddedEntity;
        if (!isField && !isEntityReference && !isEmbeddedEntity) return;
        editor.updateTriggerSearchSync(element.dndId, checked);
    };

    const handleProtectedChange = (checked: boolean) => {
        const isField = element.uiElementType === UIElementType.Field;
        const isEntityReference = element.uiElementType === UIElementType.EntityReference;
        const isEmbeddedEntity = element.uiElementType === UIElementType.EmbeddedEntity;
        if (!isField && !isEntityReference && !isEmbeddedEntity) return;

        const fieldId = isField ? (element as UIField).fieldId : undefined;
        const entityRefId = (isEntityReference || isEmbeddedEntity)
            ? `${(element as UIEntityReference).entityId}:${(element as UIEntityReference).entityGroupId || ''}`
            : undefined;
        editor.updateProtected(fieldId, entityRefId, checked);
    };

    const isSearchSyncSupported = element.uiElementType === UIElementType.Field ||
        element.uiElementType === UIElementType.EntityReference ||
        element.uiElementType === UIElementType.EmbeddedEntity;

    const isProtectionSupported = element.uiElementType === UIElementType.Field ||
        element.uiElementType === UIElementType.EntityReference ||
        element.uiElementType === UIElementType.EmbeddedEntity;

    const isElementProtected = (() => {
        if (!isProtectionSupported) return false;
        const protectedConfig = layoutEditorState.protected;
        if (!protectedConfig) return false;
        
        if (element.uiElementType === UIElementType.Field) {
            const fieldId = (element as UIField).fieldId;
            return protectedConfig.protectedFieldIds?.includes(fieldId) ?? false;
        }
        if (
            element.uiElementType === UIElementType.EntityReference
            || element.uiElementType === UIElementType.EmbeddedEntity
        ) {
            const entityRefId = `${(element as UIEntityReference).entityId}:${(element as UIEntityReference).entityGroupId || ''}`;
            return protectedConfig.protectedEntityReferenceIds?.includes(entityRefId) ?? false;
        }
        return false;
    })();
    const el: any = element;
    const desc = el.fieldId
        ? fieldTypes[el.fieldId]?.description
        : el.entityId
            ? (() => {
                const entity = editor.entities.find(e => e.docId === el.entityId);
                return el.entityGroupId
                    ? entity?.recordGroups?.[el.entityGroupId]?.description
                    : entity?.description;
            })()
            : element.uiElementType === UIElementType.MetaField && el.id in MetaFieldUIDescription
                ? MetaFieldUIDescription[el.id as keyof typeof MetaFieldUIDescription]
                : isTableElement
                    ? tableType?.description
                    : undefined;

    return (
        <FlexCol
            onClick={(e) => e.stopPropagation()}
        >
            <Text style={{ width: '100%' }}>
                <Text strong>{desc?.shortLabel}</Text>&nbsp;
                <Text type="secondary">{element.uiElementType}</Text>
                {isTableElement && (
                    <Text>
                        <small>&nbsp;{(element as UIArTable).tableId}</small>
                    </Text>
                )}
                <Divider style={{ margin: 0 }} />
                {desc?.longDescription}
            </Text>
            {isContainerElement && (
                <AddElementMenu
                    variant='borderless'
                    style={{ width: '100%' }}
                />
            )}
            {isValueElement && (
                <>
                    {isVisibilitySupported && (
                        <ViewVisibilityControl
                            value={{ visibilityTypes: currentVisibilityTypes }}
                            onChange={handleVisibilityChange}
                            formTypeId={formTypeId}
                            objectKind="element"
                            objectLabel={desc?.shortLabel ?? element.uiElementType}
                            internalMarker={isTaggedInternal}
                            onOpenMatrix={handleOpenMatrix}
                            aria-label="Set field visibility types"
                        />
                    )}
                    <Checkbox
                        checked={isTaggedInternal}
                        onChange={(e) => handleInternalOnlyChange(e.target.checked)}
                        disabled={!isInternalTagEditable}
                    >
                        Internal tag (cosmetic)
                    </Checkbox>
                    <Checkbox
                        checked={isAlwaysRequired ? true : ('required' in element ? element.required : false)}
                        onChange={(e) => handleRequiredChange(e.target.checked)}
                        disabled={isAlwaysRequired}
                    >
                        Required
                    </Checkbox>
                    {isSearchSyncSupported && (
                        <Checkbox
                            checked={Boolean((element as { triggerSearchSync?: boolean }).triggerSearchSync)}
                            onChange={(e) => handleTriggerSearchSyncChange(e.target.checked)}
                        >
                            Sync to search on change
                        </Checkbox>
                    )}
                    {isProtectionSupported && (
                        <Checkbox
                            checked={isElementProtected}
                            onChange={(e) => handleProtectedChange(e.target.checked)}
                        >
                            Protected (backend only)
                        </Checkbox>
                    )}
                </>
            )}
            {isTableElement && tableType && (
                <>
                    <Button
                        icon={<SettingOutlined />}
                        onClick={() => setColumnConfigOpen(true)}
                        aria-label="Configure table columns"
                    >
                        Configure Columns
                    </Button>
                    <Drawer
                        title="Configure Columns"
                        placement="right"
                        size={width}
                        maxSize={panelMaxWidth('layoutColumnConfig')}
                        resizable={{ onResize: (next) => setPanelWidth('layoutColumnConfig', next) }}
                        styles={{
                            dragger: drawerDraggerStyle(token),
                            wrapper: { maxWidth: panelViewportClamp('layoutColumnConfig') },
                        }}
                        onClose={() => setColumnConfigOpen(false)}
                        open={columnConfigOpen}
                        destroyOnHidden
                    >
                        <ColumnConfigEditor element={element} tableType={tableType} />
                    </Drawer>
                </>
            )}
            {isButtonElement && buttonElement && (
                <>
                    <Select
                        allowClear
                        placeholder="Full Workflow menu"
                        style={{ width: '100%' }}
                        value={buttonElement.menuSection}
                        onChange={(menuSection?: WorkflowMenuSection) =>
                            editor.updateButtonConfig(element.dndId, { menuSection, menuItemKey: '', requiresConfirmation: false })
                        }
                        options={sectionOptions}
                        aria-label="Workflow menu section"
                    />
                    {buttonElement.menuSection && (
                        <Select
                            allowClear
                            placeholder="Whole section (dropdown)"
                            style={{ width: '100%' }}
                            value={buttonElement.menuItemKey}
                            onChange={(menuItemKey?: string) =>
                                editor.updateButtonConfig(element.dndId, menuItemKey ? { menuItemKey } : { menuItemKey: '', requiresConfirmation: false })
                            }
                            options={buttonItemOptions}
                            aria-label="Specific menu item"
                        />
                    )}
                    <Input
                        placeholder="Button label"
                        value={buttonElement.label}
                        onChange={(e) =>
                            editor.updateButtonConfig(element.dndId, { label: e.target.value })
                        }
                        aria-label="Button label"
                    />
                    <Checkbox
                        checked={Boolean(buttonElement.requiresConfirmation)}
                        disabled={!buttonElement.menuItemKey}
                        onChange={(e) =>
                            editor.updateButtonConfig(element.dndId, { requiresConfirmation: e.target.checked })
                        }
                    >
                        Require confirmation
                    </Checkbox>
                </>
            )}
            <FlexCol alignEnd>
            <Button
                type="text"
                danger={!isAlwaysRequired}
                disabled={isAlwaysRequired}
                icon={<DeleteOutlined />}
                onClick={(e) => {
                    e.stopPropagation();
                    if (parentElement && !isAlwaysRequired) {
                        removeElement(parentElement as EditorUIContainer, element.dndId);
                    }
                }}
            >
                Remove
            </Button>
            </FlexCol>
        </FlexCol>
    );
};

export default LayoutEditorControls; 